import { inferFundingFeeType } from './buildFundingFeeSection';
import {
  DEFAULT_COHORT_CASH_FLOW_FOOTNOTE,
  DEFAULT_SECURITY_WORDING,
} from './buildRepaymentSection';
import { todayIsoDate } from './termSheetFormatters';

export const TERM_SHEET_SCHEMA_VERSION = 1;

export const TERM_SHEET_FIELD_KEYS = [
  'companyName',
  'companyJurisdiction',
  'companyFileNumber',
  'maxOutstandingInvestment',
  'expandedAmount',
  'monthlyCohorts',
  'maxAmountPerCohort',
  'pctOfFunding',
  'maxPctIncreaseFromLast',
  'maxAmountIncreaseFromLast',
  'minInvestment',
  'maxCac',
  'termMonths',
  'termCohorts',
  'roasMinimumEnabled',
  'roasThresholds',
  'momDeltaPct',
  'fundingFeeType',
  'fundingFeeUsd',
  'fundingFeePct',
  'fundingFeeInstallments',
  'minLiquidityMonths',
  'minLiquidityUsd',
  'nonUtilizationFeeEnabled',
  'nonUtilizationFeeWording',
  'warrantsEnabled',
  'warrantsWording',
  'cohortCashFlowFootnote',
  'securityWording',
  'cappedReturnIrr',
  'cappedReturnTotalFeePerCohort',
  'confidentialityHasNda',
  'confidentialityNdaDate',
  'noShopDays',
  'costsAndExpensesMaxCharges',
  'costsAndExpensesUpfrontFee',
];

export function createEmptyRoasThreshold(id = 1) {
  return { id, month: '', minRoasPct: '' };
}

export function createDefaultTermSheetFields() {
  return {
    companyName: '',
    companyJurisdiction: '',
    companyFileNumber: '',
    maxOutstandingInvestment: '',
    expandedAmount: '',
    monthlyCohorts: '',
    maxAmountPerCohort: '',
    pctOfFunding: '',
    maxPctIncreaseFromLast: '',
    maxAmountIncreaseFromLast: '',
    minInvestment: '',
    maxCac: '',
    termMonths: '',
    termCohorts: '',
    roasMinimumEnabled: false,
    roasThresholds: [createEmptyRoasThreshold()],
    momDeltaPct: '',
    fundingFeeType: '',
    fundingFeeUsd: '',
    fundingFeePct: '',
    fundingFeeInstallments: '',
    minLiquidityMonths: '',
    minLiquidityUsd: '',
    nonUtilizationFeeEnabled: false,
    nonUtilizationFeeWording: '',
    warrantsEnabled: false,
    warrantsWording: '',
    cohortCashFlowFootnote: DEFAULT_COHORT_CASH_FLOW_FOOTNOTE,
    securityWording: DEFAULT_SECURITY_WORDING,
    cappedReturnIrr: '',
    cappedReturnTotalFeePerCohort: '',
    confidentialityHasNda: false,
    confidentialityNdaDate: todayIsoDate(),
    noShopDays: 60,
    costsAndExpensesMaxCharges: '',
    costsAndExpensesUpfrontFee: '',
  };
}

export function createDefaultTermSheetPayload({ sourceModelId = null } = {}) {
  const fieldSources = Object.fromEntries(
    TERM_SHEET_FIELD_KEYS.map((key) => [key, { source: 'manual' }])
  );
  return {
    schemaVersion: TERM_SHEET_SCHEMA_VERSION,
    fields: createDefaultTermSheetFields(),
    sources: {
      quickCashflowModelId: sourceModelId,
      quickCashflowModelName: null,
      prefilledAt: null,
    },
    fieldSources,
  };
}

export function parseTermSheetPayload(payloadJson) {
  if (!payloadJson) {
    return createDefaultTermSheetPayload();
  }
  try {
    const parsed = typeof payloadJson === 'string' ? JSON.parse(payloadJson) : payloadJson;
    const defaults = createDefaultTermSheetPayload();
    const mergedFields = {
      ...defaults.fields,
      ...(parsed.fields || {}),
      roasThresholds:
        parsed.fields?.roasThresholds?.length > 0
          ? parsed.fields.roasThresholds
          : defaults.fields.roasThresholds,
    };
    if (parsed.fields?.roasMinimumEnabled === undefined) {
      mergedFields.roasMinimumEnabled = hasRoasMinimumData(mergedFields);
    } else {
      mergedFields.roasMinimumEnabled = Boolean(parsed.fields.roasMinimumEnabled);
    }
    if (parsed.fields?.fundingFeeType === undefined) {
      mergedFields.fundingFeeType = inferFundingFeeType(mergedFields);
    } else {
      mergedFields.fundingFeeType = parsed.fields.fundingFeeType || '';
    }
    return {
      ...defaults,
      ...parsed,
      fields: mergedFields,
      sources: {
        ...defaults.sources,
        ...(parsed.sources || {}),
      },
      fieldSources: {
        ...defaults.fieldSources,
        ...(parsed.fieldSources || {}),
      },
    };
  } catch {
    return createDefaultTermSheetPayload();
  }
}

export function serializeTermSheetPayload(payload) {
  return JSON.stringify(payload);
}

export function computeTotalCohortInvestment(fields) {
  const cohorts = Number(fields.monthlyCohorts);
  const perCohort = Number(fields.maxAmountPerCohort);
  if (!Number.isFinite(cohorts) || !Number.isFinite(perCohort)) {
    return null;
  }
  return cohorts * perCohort;
}

export function hasRoasMinimumData(fields) {
  const hasMomDelta =
    fields.momDeltaPct !== '' &&
    fields.momDeltaPct != null &&
    Number.isFinite(Number(fields.momDeltaPct));
  const hasThresholdRows = (fields.roasThresholds ?? []).some(
    (row) =>
      row.month !== '' &&
      row.month != null &&
      row.minRoasPct !== '' &&
      row.minRoasPct != null
  );
  return hasThresholdRows || hasMomDelta;
}

export function hasFilledRoasCurve(fields) {
  return Boolean(fields.roasMinimumEnabled) && hasRoasMinimumData(fields);
}
